import { CUSTOMER_APIS } from "@/api_framework/api_config";
import { IBookingRescheduleBody, IUserBookingsListData } from "@/api_framework/api_modals/Bookings";
import ModalInformation from "@/app/components/ui/Modals/Information";
import CircleSpinnerLoader from "@/app/components/ui/Reusable/LoaderComponent/CircleSpinnerLoader";
import MandatoryLabel from "@/app/components/ui/Reusable/MandatoryLabel";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import SelectMenuPlaceholder from "@/app/components/ui/SelectMenus/SelectMenuPlaceholder";
import Toggle from "@/app/components/ui/Toggles";
import { useBookingAlternativCouponListAPI } from "@/app/hooks/api_hooks/bookings/useBookingAlternativeCouponListAPI";
import { useBookingDiscountCouponListAPI } from "@/app/hooks/api_hooks/bookings/useBookingDiscountCouponListAPI";
import { useBookingMembershipListAPI } from "@/app/hooks/api_hooks/bookings/useBookingMembershipListAPI";
import { useBookingRescheduleAndCancelAPI } from "@/app/hooks/api_hooks/bookings/useBookingRescheduleAndCancelationAPI";
import { useUserBookingsListAPI } from "@/app/hooks/api_hooks/bookings/useUserBookingsListApi";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import { useModalState } from "@/app/hooks/ui_hooks/useModalState";
import { useMappedConstants } from "@/app/hooks/useMappedConstants";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { useUtils } from "@/app/hooks/useUtils";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { ISlotsState } from "@/app_redux/reducers/slices/auth/slots_slice";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";

export interface IEditBookingCommentModalState {
  bookingDetails: IUserBookingsListData;
}

export interface IBookingCommentsFormFields {
  coupon: string;
  alternativeCoupon: string;
  membershipName: string;
  otherComment: string;
  lateBooking: boolean;
  lateBookingStartTime: string;
  vaccination: boolean;
  hygienePackage: boolean;
}

const EditBookingCommentModal = () => {
  const { bookingDetails } = useModalState(
    "CUSTOMER_PROFILE_APPOINTMENTS_TABLE_COMMENTS_EDIT",
  ) as IEditBookingCommentModalState;

  const { setInvisible } = useModalActions("CUSTOMER_PROFILE_APPOINTMENTS_TABLE_COMMENTS_EDIT");

  const { getRouteKey } = useRouter();
  const slotsRouteId = getRouteKey("SLOTS", "id");
  const customerProfileRouteId = getRouteKey("CUSTOMER_PROFILE", "id");
  const { routeState: SRS } = useRouteInfo(slotsRouteId);
  const { routeState: CPRS } = useRouteInfo(customerProfileRouteId);

  const { convertDataToHTMLList } = useUtils();
  const { appointmentNotesTags, appointmentBookingCommentEditStatusMapping } = useMappedConstants();

  const { customerMembershipStatus, customerId, petId, appointmentFilter } = CPRS.state as ICustomerProfileState;

  const { couponsData, alternativeCouponsData } = SRS.state as ISlotsState;

  const [membershipStaticData, setMembershipStaticData] = useState([]);
  const [loader, setLoader] = useState(false);

  const membershipAPI = useBookingMembershipListAPI();
  const couponsAPI = useBookingDiscountCouponListAPI();
  const alternativCouponsAPI = useBookingAlternativCouponListAPI();
  const { execute: updateBooking } = useBookingRescheduleAndCancelAPI();
  const fetchCustomerBookingsAPI = useUserBookingsListAPI();
  const isAppointmentsTab = location.pathname.includes("appointments");

  const shouldHideHygiene = bookingDetails?.service_details?.service_type === "grooming";

  useEffect(() => {
    async function fetchData() {
      try {
        const response = await fetch(CUSTOMER_APIS.MEMBERSHIP_STATIC_LIST.baseURL);
        if (!response.ok) {
          throw new Error("Failed to fetch data");
        }
        const data = await response.json();
        return data;
      } catch (error) {
        console.error("Error fetching data:", error.message);
        return null;
      }
    }

    // Example usage:
   isAppointmentsTab&&fetchData()
      .then((data) => {
        if (data) {
          setMembershipStaticData(data?.data?.page_content?.body?.main?.membership);
          // Do something with the fetched data
        } else {
          // DO Seomthing
        }
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  }, []);

  const constructAppointmentNotes = (commentsData): any[] => {
    const allowedCommentKeys = new Set(["coupon", "vaccination", "otherComment"]);
    const resolveCommentKey = (key: string) => {
      if (commentsData[key] === "" || commentsData[key] === undefined) {
        return null;
      } else if (commentsData[key] === "Not Applicable") {
        return " NA";
      } else if (commentsData[key] === true) {
        return "Yes";
      } else {
        return commentsData[key];
      }
    };

    if (Object.keys(commentsData)?.length > 0) {
      const appointmentNotes = [];
      Object.keys(commentsData).forEach((commentKey) => {
        if (!allowedCommentKeys.has(commentKey)) return;
        const resolvedValue = resolveCommentKey(commentKey);
        if (resolvedValue !== null) {
          appointmentNotes.push({
            value: `${appointmentNotesTags[commentKey]}:${resolvedValue}`,
          });
        }
      });
      return appointmentNotes;
    }

    return [];
  };

  /* Fetch initial Data */
  useEffect(() => {
    if (isAppointmentsTab) {
      membershipAPI();
      const couponParams = {
        user_id: bookingDetails?.user_id || customerId,
        pet_id: bookingDetails?.pet_details?.id || petId,
        sub_service_id: bookingDetails?.subservice_details?.id,
        clinic_id: bookingDetails?.clinic_details?.id,
      };
      if (couponParams.user_id && couponParams.pet_id && couponParams.sub_service_id && couponParams.clinic_id) {
        couponsAPI(couponParams);
      }
      // alternativCouponsAPI();
    }
  }, [bookingDetails,isAppointmentsTab]);

  const formHook = useForm<IBookingCommentsFormFields>({
    mode: "onChange",
    defaultValues: {},
  });

  const couponsItem =
    couponsData?.map((c) => ({
      title: c.coupon_name,
      id: c.coupon_id,
      state: { coupon_visibility: c.coupon_visibility },
    })) ?? [];
  const alternativeCouponsItem = useSelectMenuReducer(alternativeCouponsData, "item", "id");
  const packageNamesItem = useSelectMenuReducer(membershipStaticData, "item", "id");

  const onSubmit = (data: IBookingCommentsFormFields) => {
    setLoader(true);
    const pattern = /[a-zA-Z0-9\s%||]+/g;
    const extractedComment = data?.otherComment?.match(pattern)?.join("") || "";

    const updatedData: IBookingCommentsFormFields = { ...data, otherComment: extractedComment };
    const appointmentNotes = constructAppointmentNotes(updatedData);

    const editedComment = convertDataToHTMLList(appointmentNotes);
    const booking_statuson_mappping =
      appointmentBookingCommentEditStatusMapping[bookingDetails?.booking_status?.toLowerCase()];

    const constructedBody: IBookingRescheduleBody = {
      source_module: "profile_module",
      booking_date: bookingDetails?.booking_start_date,
      booking_end_date_time: bookingDetails?.booking_end_date_time,
      booking_start_date_time: bookingDetails?.booking_start_date_time,
      booking_status: booking_statuson_mappping,
      clinic_details: {
        id: bookingDetails?.clinic_details?.id,
      },
      end_time: bookingDetails?.booking_end_time,
      start_time: bookingDetails?.booking_start_time,
      pet_details: {
        id: bookingDetails?.pet_details?.id,
      },
      service_details: {
        service_id: bookingDetails?.service_details?.id,
        service_name: bookingDetails?.service_details?.name,
        sub_service_id: bookingDetails?.subservice_details?.id,
        sub_service_name: bookingDetails?.subservice_details?.name,
      },
      staff_details: {
        id: bookingDetails?.staff_details?.id,
      },
      user_details: {
        id: bookingDetails?.user_id,
      },
      comments: editedComment,
    };
    updateBooking(constructedBody, bookingDetails?.booking_id).then(({ status }) => {
      if (status) {
        toast.success("Booking Comments Updated.");

        setTimeout(() => {
          fetchCustomerBookingsAPI(customerId, petId, {
            append: false,
            filter_by: appointmentFilter.toLowerCase(),
            limit: 10,
            page: 1,
          });
          setLoader(false);
          setInvisible();
        }, 2000);
      } else {
        toast.success("Booking Comments Updation Failed.");
      }
    });
  };

  // useEffect(() => {

  // }, [bookingDetails]);

  const fetchCommentDetails = () => {
    const cleanCommentsArray = (comments) => {
      return comments?.map((comment) => comment.trim());
    };

    const CoomentData = bookingDetails?.appointment_note;

    const split_item = bookingDetails?.appointment_note?.startsWith("<ol>")
      ? CoomentData?.split("</li>")
      : CoomentData?.split("||");

    let modifiedArray = bookingDetails?.appointment_note?.startsWith("<ol>")
      ? split_item?.map((item) => item?.replace(/<li>|<\/li>|<\/?ol>/g, ""))
      : cleanCommentsArray(split_item?.map((item) => item?.replace(/<li>|<\/li>|<\/?ol>/g, "")));

    //final price
    const Final_price = modifiedArray?.find((item) => item?.includes("Discount Coupon:"));
    const final_price = Final_price?.split(":")[1];

    if (Final_price) {
      formHook.setValue("coupon", final_price);
      modifiedArray = modifiedArray.filter((item) => item !== Final_price);
    }

    // Alternative Discount
    const alternative_discount = modifiedArray?.find((item) => item?.includes("Alternative Service Discount:"));
    const alternative_discount_percent = alternative_discount?.split(":")[1];
    if (alternative_discount_percent) {
      formHook.setValue("alternativeCoupon", alternative_discount_percent);
      modifiedArray = modifiedArray.filter((item) => item !== alternative_discount);
    }

    //Membership
    const membership = modifiedArray?.find((item) => item?.includes("Membership Pitched to Client:"));
    const membership_name = membership?.split(":")[1];
    if (membership_name) {
      formHook.setValue("membershipName", membership_name);
      modifiedArray = modifiedArray.filter((item) => item !== membership);
    }

    //vaccination diary
    const vaccination = modifiedArray?.find((item) => item?.includes("Client will bring Vaccination Diary:"));
    const vaccination_flage = vaccination?.split(":")[1];
    if (vaccination_flage === "Yes") {
      formHook.setValue("vaccination", true);
      modifiedArray = modifiedArray.filter((item) => item !== vaccination);
    }

    // Hygien package
    const hygin = modifiedArray?.find((item) => item?.includes("Hygiene package add-on required:"));
    const hygin_flage = hygin?.split(":")[1];
    if (hygin_flage === "Yes") {
      formHook.setValue("hygienePackage", true);
      modifiedArray = modifiedArray.filter((item) => item !== hygin);
    }

    //other text

    //anything that not have key to show in other

    // Join the filtered strings into a single string
    // const singleString = modifiedArray?.join(" • ");
    // if (singleString) {
    //   formHook.setValue("otherComment", singleString);
    // }
    const cleanedArray = modifiedArray
      ?.filter((item) => item.trim() !== "")
      ?.map((item) => item.replace(/^Other:/, "").replace(/<\/?b>/g, ""));

    if (cleanedArray) {
      formHook.setValue("otherComment", cleanedArray?.join(" • "));
    }
  };

  return (
    <ModalInformation
      onVisibleCallback={fetchCommentDetails}
      modalId="CUSTOMER_PROFILE_APPOINTMENTS_TABLE_COMMENTS_EDIT"
      contentOverflow
      title={`Edit Comments`}
      dialogClassName="w-full sm:w-[90%] sm:max-w-2xl sm:my-6 sm:p-5 md:max-w-3xl md:my-8 md:p-6 !translate-x-0"
    >
      <div className="px-1 sm:px-2">
        <form
          onSubmit={formHook.handleSubmit(onSubmit, (e) => {
            console.log(e, "Ddd");
          })}
        >
          <div className="mx-auto mt-2">
            <div className="w-full space-y-2">
              <div>
                <div className="grid grid-cols-1 md:grid-cols-[220px_minmax(0,1fr)] items-center gap-2 md:gap-3">
                  <div className="p-2 text-black text-xs">
                    <MandatoryLabel label={"Discount Coupons"} isMandatory />
                  </div>
                  <div className="p-2 text-xs">
                    <SearchableSelectMenu
                      errorMessages={[{ message: "Coupon is required", type: "required" }]}
                      onSelectItem={(item) => {
                        if (item) {
                          formHook.setValue("coupon", item.title);
                        } else {
                          formHook.setValue("coupon", "");
                        }
                      }}
                      filterOnEmptyQuery={(item) => item.state?.coupon_visibility !== false}
                      onEnterPress={(typedValue) => {
                        const exactMatch = couponsData?.find(
                          (c) => c.coupon_name?.toLowerCase() === typedValue?.toLowerCase(),
                        );
                        if (exactMatch) {
                          formHook.setValue("coupon", exactMatch.coupon_name);
                        } else {
                          formHook.setValue("coupon", "");
                          toast.error("Coupon not applicable");
                        }
                      }}
                      fieldError={formHook.formState.errors.coupon}
                      register={formHook.register("coupon", { required: true })}
                      selectItems={couponsItem}
                      placeholder="Select Coupons"
                      showTooltips={true}
                      showTypedErrors={false}
                      showDropdownIcon={true}
                      defaultSelected={couponsItem?.filter((oc) => oc.title === formHook.watch("coupon"))[0]}
                      listBoxClassName=""
                      className="text-gray-800"
                      containerClassName="w-full"
                    />
                  </div>
                </div>
                {/* <div className="grid grid-cols-1 md:grid-cols-[220px_minmax(0,1fr)] items-center gap-2 md:gap-3">
                  <div className="p-2 text-black text-xs">
                    <MandatoryLabel label="Alternative Service Discount" />
                  </div>
                  <div className="p-2 text-xs">
                    <SearchableSelectMenu
                      errorMessages={[{ message: "Clinic is required", type: "required" }]}
                      onSelectItem={(item) => {
                        if (item) {
                          formHook.setValue("alternativeCoupon", item.title);
                        }
                      }}
                      fieldError={formHook.formState.errors.alternativeCoupon}
                      register={formHook.register("alternativeCoupon", { required: false })}
                      selectItems={alternativeCouponsItem}
                      placeholder="Select Coupons"
                      showTooltips={true}
                      showTypedErrors={false}
                      showDropdownIcon={true}
                      defaultSelected={
                        alternativeCouponsItem?.filter((oc) => oc.title === formHook.watch("alternativeCoupon"))[0]
                      }
                      className="text-gray-800"
                      containerClassName="w-full"
                    />
                  </div>
                </div> */}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-[220px_minmax(0,1fr)] items-center gap-2 md:gap-3">
                <div className="p-2 text-black text-xs">
                  <MandatoryLabel label={"Membership Pitched to Client"} />
                </div>
                <div className="p-2 text-xs">
                  <SearchableSelectMenu
                    errorMessages={[{ message: "Clinic is required", type: "required" }]}
                    onSelectItem={(item) => {
                      if (item) {
                        formHook.setValue("membershipName", item.title);
                      }
                    }}
                    fieldError={formHook.formState.errors.membershipName}
                    register={formHook.register("membershipName", { required: false })}
                    selectItems={packageNamesItem}
                    placeholder="Select Membership"
                    showTooltips={true}
                    showTypedErrors={false}
                    showDropdownIcon={true}
                    defaultSelected={packageNamesItem?.filter((oc) => oc.title === formHook.watch("membershipName"))[0]}
                    listBoxClassName="w-full"
                    className="text-gray-800"
                    containerClassName="w-full"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-[220px_minmax(0,1fr)] items-center gap-2 md:gap-3">
                <div className="p-2 text-black text-xs">Client will bring Vaccination Diary</div>
                <div className="p-2 text-xs flex items-center">
                  <input hidden {...formHook.register("vaccination", { required: false })} />
                  <Toggle
                    onToggle={(state: boolean) => {
                      formHook.setValue("vaccination", state);
                    }}
                    checked={formHook.watch("vaccination")}
                  />
                </div>
              </div>
              {shouldHideHygiene && (
                <div className="grid grid-cols-1 md:grid-cols-[220px_minmax(0,1fr)] items-center gap-2 md:gap-3">
                  <div className="p-2 text-black text-xs">
                    <MandatoryLabel label={"Hygiene package add-on required"} />
                  </div>
                  <div className="p-2 text-xs flex items-center">
                    <input hidden {...formHook.register("hygienePackage", { required: false })} />
                    <Toggle
                      onToggle={(state: boolean) => {
                        formHook.setValue("hygienePackage", state);
                      }}
                      checked={formHook.watch("hygienePackage")}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="px-2 sm:px-3 mt-2">
            <label htmlFor="comment" className="block font-normal text-black text-xs">
              Add Comments (Optional)
            </label>
            <div className="mt-2">
              <textarea
                rows={5}
                {...formHook.register("otherComment", { required: false })}
                className="block border-0 bg-[#F5F5F5] shadow-sm py-1.5 rounded-md w-full text-gray-900 text-xs"
                defaultValue={""}
              />
            </div>
          </div>

          <div className="px-2 sm:px-3 mt-6 flex justify-end">
            <button
              type="submit"
              className="min-w-[140px] rounded-md bg-black px-4 py-2.5 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black"
            >
              {loader ? (
                <div className="flex justify-center">
                  <CircleSpinnerLoader h="20" w="20" />
                </div>
              ) : (
                "Update Comment"
              )}
            </button>
          </div>
        </form>
      </div>
    </ModalInformation>
  );
};
export default EditBookingCommentModal;
